import { hasProp } from '@atlassian/jira-shared-types/src/narrowing.tsx';
import {
	AGENT,
	CUSTOMER,
	PROJECT_ADMIN,
	type SUPPORTED_WHO_CHOOSES_APPROVERS,
} from './approval-step/types.tsx';
import type { ApprovalStepData, Approver } from './types.tsx';

export const getNumberOfApprovers = (
	conditionType: string,
	conditionValue: string,
	defaultApproversCount: number,
): number => {
	if (conditionType === 'number') {
		return Number(conditionValue);
	}
	if (defaultApproversCount > 0) {
		return defaultApproversCount;
	}
	return 1;
};

export const getWhoChoosesApprovers = (
	serviceDeskFieldCategory: string,
	defaultApproversCount: number,
): SUPPORTED_WHO_CHOOSES_APPROVERS => {
	if (serviceDeskFieldCategory === 'request') {
		return CUSTOMER;
	}
	if (defaultApproversCount > 0) {
		return PROJECT_ADMIN;
	}
	return AGENT;
};

const transformDefaultApprover = (acc: Approver[], unknownApprover: unknown) => {
	if (!unknownApprover || typeof unknownApprover !== 'object') {
		return acc;
	}

	if (
		hasProp(unknownApprover, 'id') &&
		typeof unknownApprover.id === 'string' &&
		hasProp(unknownApprover, 'type') &&
		typeof unknownApprover.type === 'string'
	) {
		const approver: Approver = {
			id: unknownApprover.id,
			type: unknownApprover.type,
		};

		acc.push(approver);
	}

	return acc;
};

export const transformUnknownDataToApprovalStepData = (
	unknownData: unknown,
): ApprovalStepData | null => {
	if (!unknownData || typeof unknownData !== 'object') {
		return null;
	}

	const approvalStepData: ApprovalStepData = {
		statusId: '',
		customFieldName: '',
		conditionType: '',
		conditionValue: '',
		defaultApprovers: [],
		serviceDeskFieldCategory: '',
		statusName: '',
	};

	if (hasProp(unknownData, 'statusId') && typeof unknownData.statusId === 'string') {
		approvalStepData.statusId = unknownData.statusId;
	}

	if (hasProp(unknownData, 'conditionType') && typeof unknownData.conditionType === 'string') {
		approvalStepData.conditionType = unknownData.conditionType;
	}

	if (hasProp(unknownData, 'conditionValue') && typeof unknownData.conditionValue === 'string') {
		approvalStepData.conditionValue = unknownData.conditionValue;
	}

	if (hasProp(unknownData, 'defaultApprovers') && Array.isArray(unknownData.defaultApprovers)) {
		const defaultApprovers = unknownData.defaultApprovers.reduce(transformDefaultApprover, []);

		approvalStepData.defaultApprovers = defaultApprovers;
	}

	if (
		hasProp(unknownData, 'serviceDeskFieldCategory') &&
		typeof unknownData.serviceDeskFieldCategory === 'string'
	) {
		approvalStepData.serviceDeskFieldCategory = unknownData.serviceDeskFieldCategory;
	}

	if (hasProp(unknownData, 'statusName') && typeof unknownData.statusName === 'string') {
		approvalStepData.statusName = unknownData.statusName;
	}

	if (hasProp(unknownData, 'customFieldName') && typeof unknownData.customFieldName === 'string') {
		approvalStepData.customFieldName = unknownData.customFieldName;
	}

	return approvalStepData;
};

export const getApprovalStepData = (rawDataItem: unknown) => {
	const approvalStepData: ApprovalStepData | null =
		transformUnknownDataToApprovalStepData(rawDataItem);

	if (approvalStepData == null) {
		return null;
	}

	const {
		statusId,
		customFieldName,
		conditionType,
		conditionValue,
		defaultApprovers,
		serviceDeskFieldCategory,
		statusName,
	} = approvalStepData;

	const numberOfApprovers: number = getNumberOfApprovers(
		conditionType,
		conditionValue,
		defaultApprovers.length,
	);
	const whoChoosesApprovers = getWhoChoosesApprovers(
		serviceDeskFieldCategory,
		defaultApprovers.length,
	);
	const defaultApproverIds = defaultApprovers.map(({ id }) => id);

	return {
		statusId,
		customFieldName,
		statusName,
		numberOfApprovers,
		whoChoosesApprovers,
		defaultApproverIds,
	};
};
